import React, { createContext, useState, useContext, useEffect, ReactNode, useRef } from 'react';
import authApi, { AuthResponse, UserProfile, SystemLoginRequest, GoogleLoginRequest } from '../services/authApi';
import { toast } from 'react-toastify';

const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenExpiringSoon = (token: string, bufferMinutes: number = 5): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  const timeUntilExpiry = decoded.exp * 1000 - Date.now();
  return timeUntilExpiry <= bufferMinutes * 60 * 1000;
};

interface User {
  userId: number;
  name: string;
  email: string | null;
  username: string | null;
  userType: 'CLIENT' | 'SYSTEM';
  roles: string[];
  privileges: string[];
  profilePhoto: string | null;
  isPremium: boolean;
  planName?: string;
  expiryDate?: string;
  daysRemaining?: number;
  createdDate?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: SystemLoginRequest) => Promise<void>;
  googleLogin: (googleIdToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadUser = async () => {
      const storedUser = localStorage.getItem('user');
      const accessToken = authApi.getAccessToken();
      if (storedUser && accessToken) {
        try {
          const response = await authApi.getUserProfile();
          const profile = response.data;
          const subResponse = await authApi.getSubscription();
          const subscriptionData = subResponse.data;
          const userData: User = {
            userId: profile.userId,
            name: profile.name,
            email: profile.email,
            username: profile.userType === 'SYSTEM' ? profile.name : null,
            userType: profile.userType,
            roles: profile.roles,
            privileges: profile.privileges,
            profilePhoto: profile.profilePhoto,
            isPremium: subscriptionData.isPremium,
            planName: subscriptionData.planName ?? undefined,
            expiryDate: subscriptionData.expiryDate ?? undefined,
            daysRemaining: subscriptionData.daysRemaining,
            createdDate: subscriptionData.createdDate ?? undefined,
          };
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch {
          authApi.clearTokens();
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };
    loadUser();
  }, []);

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const accessToken = authApi.getAccessToken();
      const refreshToken = localStorage.getItem('refreshToken');
      if (!accessToken || !refreshToken) {
        if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current);
        return;
      }
      if (isTokenExpiringSoon(accessToken, 5)) {
        try {
          const response = await authApi.refreshToken({ refreshToken });
          authApi.saveTokens(response.data.accessToken, response.data.refreshToken);
        } catch {
          toast.error('Session expired. Please login again.');
          authApi.clearTokens();
          localStorage.removeItem('user');
          setUser(null);
          if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current);
          window.location.href = '/';
        }
      }
    };

    if (user) {
      checkAndRefreshToken();
      tokenRefreshIntervalRef.current = setInterval(checkAndRefreshToken, 60 * 1000);
    } else {
      if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current);
    }
    return () => { if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current); };
  }, [user]);

  const buildUser = async (authData: AuthResponse): Promise<User> => {
    const [subResponse, profileResponse] = await Promise.all([
      authApi.getSubscription(),
      authApi.getUserProfile(),
    ]);
    const sub = subResponse.data;
    const profile = profileResponse.data;
    return {
      userId: authData.userId,
      name: authData.name,
      email: authData.email,
      username: authData.username,
      userType: authData.userType,
      roles: authData.roles,
      privileges: authData.privileges,
      profilePhoto: profile.profilePhoto,
      isPremium: sub.isPremium,
      planName: sub.planName ?? undefined,
      expiryDate: sub.expiryDate ?? undefined,
      daysRemaining: sub.daysRemaining,
      createdDate: sub.createdDate ?? undefined,
    };
  };

  const login = async (credentials: SystemLoginRequest) => {
    try {
      const response = await authApi.systemLogin({ ...credentials, source: 'COMMUNITY' });
      authApi.saveTokens(response.data.accessToken, response.data.refreshToken);
      const userData = await buildUser(response.data);
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      toast.success('Login successful!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid username or password');
      throw error;
    }
  };

  const googleLogin = async (googleIdToken: string) => {
    try {
      const response = await authApi.googleLogin({ googleIdToken, source: 'COMMUNITY' });
      authApi.saveTokens(response.data.accessToken, response.data.refreshToken);
      const userData = await buildUser(response.data);
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      toast.success('Welcome to SwingRadarX Academy!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Google login failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      authApi.clearTokens();
      localStorage.removeItem('user');
    }
    setUser(null);
    toast.success('Logged out successfully');
  };

  const refreshUserProfile = async () => {
    const response = await authApi.getUserProfile();
    const profile = response.data;
    const subResponse = await authApi.getSubscription();
    const sub = subResponse.data;
    const userData: User = {
      userId: profile.userId,
      name: profile.name,
      email: profile.email,
      username: profile.userType === 'SYSTEM' ? profile.name : null,
      userType: profile.userType,
      roles: profile.roles,
      privileges: profile.privileges,
      profilePhoto: profile.profilePhoto,
      isPremium: sub.isPremium,
      planName: sub.planName ?? undefined,
      expiryDate: sub.expiryDate ?? undefined,
      daysRemaining: sub.daysRemaining,
      createdDate: sub.createdDate ?? undefined,
    };
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, googleLogin, logout, refreshUserProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
